import { CalendarToday, ExpandLess, KeyboardArrowDown, Print, Search } from '@mui/icons-material';
import {
  Avatar,
  Button,
  CardHeader,
  Chip,
  Drawer,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  SvgIcon,
  TextField,
  Tooltip,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { usePageView } from 'src/hooks/use-page-view';
import { BasicDateCalendar } from 'src/layouts/components/CustomDatePicker';
import { getFacilityToday, getFacilityTodayYmd } from 'src/utils/convertDateToSelectedTimeZone';
import {
  getInterventionGroupAction,
  getTodayPatientListAction,
} from 'src/redux/actions/therapist-dashboard-action';
import PrintPatient from './PrintPatient';
import TaskDetails from './task-deatails';
import ListingTable from './todays-activity-table';

import {
  getDailyNotesPrintStatus,
  getDailySessionPatientInfoV2,
  resetDailyNotesPrintStatus,
} from 'src/redux/actions/dailySessionNotes';
import SchedulePrintDialog from './SchedulePrintDialog';
import PrintScheduleModal, { SortOption } from './PrintScheduleModal';
import BulkSchedulePrintDialog from '../../schedule/therapist/UI-Improvment/BulkSchedulePrintDialog';
// THIN-8018: "today" is the facility-local day (THIN-7916 helper), not the browser day, so the
// dashboard agrees with the Daily Session Notes page about which day an evening shift belongs to.
export const isToday = (date: any) => moment(date).format('YYYY-MM-DD') === getFacilityTodayYmd();

const TodaysOverview = () => {
  usePageView();
  const dispatch = useDispatch();
  const calenderRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const getDashboardPatientList = useSelector((state: any) => state.getTodayPatientListReducer);

  const [isUsingNewSchedulePrint] = useFeatureToggle('new-schedule-print', 'ENTERPRISE');

  const getTherapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  const listOfTherapistAvailability = useSelector(
    (state: any) => state.getListOfTherapistAvaiReducer
  );

  const printStatus = useSelector((state: any) => state.getDailyNotesPrintStatusReducer?.response);
  const canPrintOwnSchedule = Boolean(printStatus?.print);
  const canPrintBulkSchedule = Boolean(printStatus?.batchPrint);

  const [open, setOpen] = useState(false);
  const [taskInfo, setTaskInfo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = listOfTherapistAvailability?.response?.data
    ?.slice()
    .sort((a: any, b: any) => a?.lastName?.localeCompare(b?.lastName))
    .filter((therapist: any) => {
      const normalizedLabel = (therapist?.lastName + therapist?.firstName)
        ?.replace(/[\s,]/g, '')
        ?.toLowerCase();
      const normalizedSearchTerm = searchTerm?.replace(/[\s,]/g, '')?.toLowerCase();

      return normalizedLabel?.includes(normalizedSearchTerm);
    });

  const onClose = () => {
    setOpen(false);
  };

  const handleOpenDrawer = (task: any) => {
    dispatch(
      getInterventionGroupAction({
        params: {
          patientId: task?.uuid,
          type: getTherapistProfile?.discipline,
        },
      })
    );
    dispatch(
      getDailySessionPatientInfoV2({
        providerId: selectedTherapist.uuid,
        patientId: task?.uuid,
        date: moment(selectedDate).format('YYYY-MM-DD'),
      })
    );
    setTaskInfo(task);
    setOpen(true);
  };

  const taskConstants: any = {
    EVALUATION: 'Evaluation',
    THERAPY: 'Therapy',
    GROUP_THERAPY: 'Group Therapy',
    INSURANCE_CALL: 'Insurance call',
    SCREENINGS: 'Screenings',
    CONCURRENT: 'Concurrent',
  };

  const [patientTaskList, setPatientTaskList] = useState<any>([]);
  const [order, setOrder] = useState('asc');
  const [orderBy] = useState('patientName');
  const [isAppointmentSortActive, setIsAppointmentSortActive] = useState(false);
  const [appointmentOrder, setAppointmentOrder] = useState('asc');

  const PAGE_SIZE = 10;
  const [, setPageNumber] = useState(0);
  const totalPages: number = getDashboardPatientList?.response?.totalPages ?? 0;
  const requestedPageRef = useRef(0);
  const lastIngestedPageRef = useRef<number>(-1);
  const inFlightRef = useRef(false);
  const lastSortParamsRef = useRef<{
    sortByName?: string;
    sortBy?: string;
    sortDirection?: string;
  }>({ sortByName: 'ASC' });

  const handleSortRequest = () => {
    const isAsc = orderBy === 'patientName' && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

    setIsAppointmentSortActive(false);
    setPageNumber(0);
    requestedPageRef.current = 0;
    lastIngestedPageRef.current = -1;
    inFlightRef.current = true;
    lastSortParamsRef.current = { sortByName: (nextOrder || '').toUpperCase() };

    dispatch(
      getTodayPatientListAction({
        therapistUuid: selectedTherapist?.uuid,
        date: formattedDate,
        sortByName: (nextOrder || '').toUpperCase(),
        page: 0,
        pageSize: PAGE_SIZE,
      })
    );

    setOrder(nextOrder);
  };

  const handleAppointmentSortRequest = () => {
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
    const nextDirection = appointmentOrder === 'asc' ? 'desc' : 'asc';

    setIsAppointmentSortActive(true);
    setAppointmentOrder(nextDirection);
    setPageNumber(0);
    requestedPageRef.current = 0;
    lastIngestedPageRef.current = -1;
    inFlightRef.current = true;
    lastSortParamsRef.current = {
      sortBy: 'APPOINTMENT',
      sortDirection: (nextDirection || '').toUpperCase(),
    };

    dispatch(
      getTodayPatientListAction({
        therapistUuid: selectedTherapist?.uuid,
        date: formattedDate,
        sortBy: 'APPOINTMENT',
        sortDirection: (nextDirection || '').toUpperCase(),
        page: 0,
        pageSize: PAGE_SIZE,
      })
    );
  };

  const loadNextPage = () => {
    if (inFlightRef.current) return;
    if (getDashboardPatientList?.loading) return;
    const nextPage = requestedPageRef.current + 1;
    if (nextPage >= totalPages) return;
    if (!selectedTherapist?.uuid) return;

    inFlightRef.current = true;
    requestedPageRef.current = nextPage;
    setPageNumber(nextPage);

    dispatch(
      getTodayPatientListAction({
        therapistUuid: selectedTherapist?.uuid,
        date: moment(selectedDate).format('YYYY-MM-DD'),
        ...lastSortParamsRef.current,
        page: nextPage,
        pageSize: PAGE_SIZE,
      })
    );
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadNextPage();
    }
  };

  useEffect(() => {
    if (getDashboardPatientList?.loading) return;
    const content = getDashboardPatientList?.response?.content;
    if (!Array.isArray(content)) return;

    if (lastIngestedPageRef.current === requestedPageRef.current) return;
    lastIngestedPageRef.current = requestedPageRef.current;
    inFlightRef.current = false;

    const rows: any[] = [];
    content.forEach((patient: any) => {
      const tasks = Array.isArray(patient?.tasks) ? patient.tasks : [];
      if (tasks.length === 0) return;
      tasks.forEach((task: any, idx: number) => {
        const taskKey = task?.taskType;
        rows.push({
          ...patient,
          tasks: undefined,
          _showPatientHeader: idx === 0,
          _patientUuid: patient?.uuid,
          uuid: patient?.uuid,
          taskMinutes: task?.minutes,
          scheduleItemType: task?.scheduleItemType,
          appointmentStartTime: task?.appointmentStart,
          appointmentEndTime: task?.appointmentEnd,
          status: task?.status,
          discipline: task?.discipline,
          therapyTypes: taskKey ? [taskKey] : [],
          taskNameWithId: taskKey ? [{ key: taskKey, value: taskConstants[taskKey] }] : [],
        });
      });
    });

    if (requestedPageRef.current === 0) {
      setPatientTaskList(rows);
    } else {
      setPatientTaskList((prev: any[]) => {
        const keyOf = (r: any) =>
          `${r?._patientUuid}|${r?.taskNameWithId?.[0]?.key ?? ''}|${
            r?.appointmentStartTime ?? ''
          }|${r?.appointmentEndTime ?? ''}`;
        const seen = new Set(prev.map(keyOf));
        const fresh = rows.filter((r) => !seen.has(keyOf(r)));
        return [...prev, ...fresh];
      });
    }
  }, [getDashboardPatientList]);
  const [isPrint, setPrint] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('ROOM_NUMBER');
  const [isBulkPrintOpen, setIsBulkPrintOpen] = useState(false);

  const handlePrint = () => {
    setSortBy('ROOM_NUMBER');
    setOpenModal(true);
  };

  const handlePrintPreview = () => {
    setOpenModal(false);
    if (isUsingNewSchedulePrint) {
      console.log('NEW schedule print!');
      setIsPrintDialogOpen(true);
    } else {
      console.log('OLD schedule print!');
      setPrint((prev) => !prev);
    }
  };

  const handleClosePrintDialog = () => {
    setIsPrintDialogOpen(false);
  };

  const handleCloseInfo = () => {
    setPrint(false);
  };
  const [selectedTherapist, setSelectedTherapist] = useState<any>();
  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handelClickMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  }, []);
  const handleCloseMenu = useCallback(() => {
    setAnchorEl1(null);
  }, []);

  const handleSelectedTherapist = useCallback((therapist: any) => {
    setSelectedTherapist(therapist);
    setAnchorEl1(null);
  }, []);

  useEffect(() => {
    if (getTherapistProfile?.providerId) {
      setSelectedTherapist({
        name: `${getTherapistProfile?.lastName} ${getTherapistProfile?.firstName}`,
        lastName: `${getTherapistProfile?.lastName}`,
        firstName: `${getTherapistProfile?.firstName}`,
        uuid: getTherapistProfile?.providerId,
        avatar: getTherapistProfile?.avatar,
        credentials: getTherapistProfile?.credentials,
      });
    }
  }, [getTherapistProfile]);

  const [openCalender, setOpenCalender] = useState(false);
  // THIN-8018: default to the facility-local day, not the browser/UTC day, so the task drawer's
  // "Document" button opens the DSN page on the day the therapist is actually working in.
  const [selectedDate, setSelectedDate] = useState<any>(getFacilityToday());
  const jsDate = moment(selectedDate, 'DD-MM-YYYY').toDate();
  const formattedDate = moment(jsDate).format('MMM D, YYYY');
  const scheduleDateString = moment(selectedDate).format('YYYY-MM-DD');
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const currentDate = moment(getFacilityToday());
  const handleClickCalender = () => {
    setOpenCalender((prev: any) => !prev);
  };

  const handleChangeDate = (e: any) => {
    setSelectedDate(new Date(e));
    setOpenCalender(false);
  };

  useEffect(() => {
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
    if (selectedTherapist?.uuid) {
      setIsAppointmentSortActive(false);
      setOrder('asc');
      setAppointmentOrder('asc');
      setPageNumber(0);
      requestedPageRef.current = 0;
      lastIngestedPageRef.current = -1;
      inFlightRef.current = true;
      lastSortParamsRef.current = { sortByName: 'ASC' };
      dispatch(
        getTodayPatientListAction({
          therapistUuid: selectedTherapist?.uuid,
          date: formattedDate,
          sortByName: 'ASC',
          page: 0,
          pageSize: PAGE_SIZE,
        })
      );
      dispatch(
        getDailyNotesPrintStatus({
          therapistUuid: selectedTherapist?.uuid,
          date: formattedDate,
        })
      );
    }
  }, [selectedTherapist, selectedDate]);

  useEffect(() => {
    if (selectedTherapist?.uuid) {
      dispatch(
        getDailyNotesPrintStatus({
          therapistUuid: selectedTherapist?.uuid,
          date: moment(selectedDate).format('YYYY-MM-DD'),
        })
      );
    }
  }, [selectedTherapist, selectedDate, getDashboardPatientList?.response]);

  useEffect(() => {
    return () => {
      dispatch(resetDailyNotesPrintStatus());
    };
  }, []);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6">
                  {' '}
                  {selectedTherapist?.uuid === getTherapistProfile?.providerId &&
                  isToday(selectedDate)
                    ? 'Today’s Patients'
                    : 'Schedule Patients'}{' '}
                </Typography>
                {patientTaskList?.length !== undefined && (
                  <Chip
                    label={`${getDashboardPatientList?.response?.totalElements} Patients`}
                    variant="outlined"
                    sx={{ marginLeft: '10px' }}
                  />
                )}
                <>
                  <Button
                    id="basic-button"
                    onClick={handelClickMenu}
                    sx={{
                      ml: 2,
                      borderColor: '#afb3ba9e',
                      color: '#111927',
                      width: 230,
                      pr: 1.5,
                      pl: 2,
                      height: '40px',
                      display: 'flex',
                      justifyContent: 'start',
                      '&:hover': {
                        color: '#111927',
                        borderColor: '#afb3ba9e',
                      },
                    }}
                    variant="outlined"
                    startIcon={
                      <Avatar
                        src={selectedTherapist?.avatar}
                        sx={{ width: 28, height: 28 }}
                      />
                    }
                    endIcon={open1 ? <ExpandLess /> : <KeyboardArrowDown />}
                    title={
                      selectedTherapist
                        ? `${selectedTherapist?.lastName} ${selectedTherapist?.firstName}`
                        : ''
                    }
                  >
                    <Typography
                      sx={{
                        width: 200,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left',
                      }}
                    >
                      {selectedTherapist?.uuid
                        ? `${selectedTherapist?.lastName} ${selectedTherapist?.firstName}`
                        : 'Assign Therapist'}
                    </Typography>
                  </Button>
                  <Stack
                    ml={2}
                    ref={calenderRef}
                  >
                    <Button
                      sx={{
                        background: '#fff',
                        borderRadius: '12px',
                        border: '1px solid rgba(17, 25, 39, 0.16)',
                        color: '#384250',
                        '&:hover': {
                          border: '1px solid rgba(17, 25, 39, 0.16)',
                        },
                      }}
                      startIcon={
                        <SvgIcon>
                          <CalendarToday />
                        </SvgIcon>
                      }
                      variant="outlined"
                      onClick={handleClickCalender}
                    >
                      {formattedDate} {moment(jsDate).isSame(currentDate, 'day') && '(Today)'}
                    </Button>
                    {openCalender && (
                      <BasicDateCalendar
                        value={selectedDate}
                        handleChangeDate={handleChangeDate}
                      />
                    )}
                  </Stack>
                </>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Box>
                  <Tooltip
                    title={!canPrintOwnSchedule ? 'No tasks are assigned to this therapist' : ''}
                  >
                    <span>
                      <Button
                        onClick={handlePrint}
                        startIcon={<Print />}
                        disabled={!canPrintOwnSchedule}
                      >
                        Print this schedule
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip
                    title={!canPrintBulkSchedule ? 'No tasks are assigned to any therapist' : ''}
                  >
                    <span>
                      <Button
                        onClick={() => setIsBulkPrintOpen(true)}
                        startIcon={<Print />}
                        disabled={!canPrintBulkSchedule}
                      >
                        Print everyone&apos;s schedules
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          }
        />
        <Box
          onScroll={handleScroll}
          sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}
        >
          <ListingTable
            handleOpenDrawer={handleOpenDrawer}
            items={patientTaskList}
            orderBy={orderBy}
            order={order}
            handleSortRequest={handleSortRequest}
            handleAppointmentSortRequest={handleAppointmentSortRequest}
            isAppointmentSortActive={isAppointmentSortActive}
            appointmentOrder={appointmentOrder}
            selectedDate={moment(selectedDate).format('YYYY-MM-DD')}
          />
        </Box>
      </Card>
      {/* Schedule Print Dialog */}
      {selectedTherapist && (
        <SchedulePrintDialog
          providerUUID={selectedTherapist?.uuid}
          dateString={scheduleDateString}
          open={isPrintDialogOpen}
          onClose={handleClosePrintDialog}
          sortOrder={sortBy}
        />
      )}
      {selectedTherapist && (
        <BulkSchedulePrintDialog
          providerUUID={selectedTherapist?.uuid}
          dateString={scheduleDateString}
          open={isBulkPrintOpen}
          onClose={() => setIsBulkPrintOpen(false)}
          scheduling
        />
      )}
      <Drawer
        anchor="right"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 532,
          },
        }}
      >
        <TaskDetails
          handleCloseDrawer={onClose}
          taskInfo={taskInfo}
          therapistUuid={selectedTherapist?.uuid}
          selectedDate={selectedDate}
        />
      </Drawer>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl1}
        open={open1}
        onClose={handleCloseMenu}
        sx={{
          width: '260px',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <TextField
          variant="outlined"
          placeholder={`Search by therapist`}
          fullWidth
          onChange={(event: any) => {
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
          sx={{
            p: '0px 5px',
            '& .MuiInputBase-input': {
              padding: '12px 0px',
            },
          }}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Grid
          height={400}
          sx={{ overflowY: 'scroll' }}
        >
          <Box sx={{ width: '220px' }}>
            {filteredOptions?.length ? (
              filteredOptions?.map((therapist: any) => (
                <MenuItem
                  key={therapist?.uuid}
                  onClick={() => handleSelectedTherapist(therapist)}
                  sx={{ display: 'flex', py: 1 }}
                >
                  <Avatar
                    src={therapist?.avatar}
                    sx={{ width: 30, height: 30 }}
                  />
                  <Tooltip
                    title={
                      therapist?.name?.length > 15
                        ? `${therapist?.lastName} ${therapist?.firstName}`
                        : ''
                    }
                  >
                    <Typography
                      sx={{
                        ml: 1,
                        overflow: 'hidden',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {`${therapist?.lastName} ${therapist?.firstName}`}
                    </Typography>
                  </Tooltip>
                </MenuItem>
              ))
            ) : (
              <Typography
                color={'text.secondary'}
                variant="subtitle2"
                sx={{
                  textAlign: 'center',
                  width: '220px',
                  p: 3,
                }}
              >
                No results found
              </Typography>
            )}
          </Box>
        </Grid>
      </Menu>

      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '2250px' } }}
        onClose={() => setPrint(false)}
        aria-labelledby="customized-dialog-title"
        open={isPrint}
      >
        <PrintPatient
          close={handleCloseInfo}
          selectedTherapist={selectedTherapist}
          patientTaskList={patientTaskList}
          selectedDate={selectedDate}
        />
      </BootstrapDialog>
      <PrintScheduleModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onPrintPreview={handlePrintPreview}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    </>
  );
};

export default TodaysOverview;
